// src/sectors/pilates/members/components/MembersToolbar.jsx
import React from 'react';
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Divider from '@mui/material/Divider';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

function KpiPill({ icon, label, value, tone = 'neutral' }) {
  const theme = useMuiTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const toneMap = {
    primary: {
      bg: 'rgba(0,150,136,.10)',
      border: 'rgba(0,150,136,.28)',
      icon: '#009688',
    },
    success: {
      bg: 'rgba(76,175,80,.10)',
      border: 'rgba(76,175,80,.28)',
      icon: theme.palette.success.main,
    },
    error: {
      bg: 'rgba(244,67,54,.09)',
      border: 'rgba(244,67,54,.26)',
      icon: theme.palette.error.main,
    },
    warning: {
      bg: 'rgba(255,193,7,.12)',
      border: 'rgba(255,193,7,.28)',
      icon: theme.palette.warning.main,
    },
    neutral: {
      bg: 'rgba(0,0,0,.03)',
      border: 'rgba(0,0,0,.10)',
      icon: theme.palette.text.primary,
    },
  };

  const t = toneMap[tone] || toneMap.neutral;

  return (
    <MDBox
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? 1 : 1.2,
        px: isMobile ? 1.1 : 1.4,
        py: isMobile ? 1.0 : 1.15,
        borderRadius: '18px',
        border: `1px solid ${t.border}`,
        background: t.bg,
        minWidth: 0,
      }}
    >
      <MDBox
        sx={{
          width: isMobile ? 30 : 34,
          height: isMobile ? 30 : 34,
          borderRadius: '12px',
          display: 'grid',
          placeItems: 'center',
          border: `1px solid rgba(0,0,0,.10)`,
          background: 'rgba(255,255,255,.78)',
          flex: '0 0 auto',
        }}
      >
        <Icon sx={{ fontSize: isMobile ? 18 : 19, color: t.icon, opacity: 0.95 }}>{icon}</Icon>
      </MDBox>

      <MDBox sx={{ minWidth: 0 }}>
        <MDTypography
          variant="caption"
          sx={{
            color: theme.palette.text.primary,
            opacity: 0.7,
            fontWeight: 800,
            lineHeight: 1.1,
            letterSpacing: '0.02em',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: isMobile ? 120 : 'unset',
          }}
        >
          {label}
        </MDTypography>

        <MDTypography
          variant={isMobile ? 'h6' : 'h6'}
          fontWeight="bold"
          sx={{
            color: theme.palette.text.primary,
            lineHeight: 1.15,
            mt: 0.25,
          }}
        >
          {value}
        </MDTypography>
      </MDBox>
    </MDBox>
  );
}

export function MembersToolbar({ totalCount, activeCount, passiveCount, debtCount }) {
  const theme = useMuiTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Card
      sx={{
        borderRadius: '22px',
        overflow: 'hidden',
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[4],
      }}
    >
      {/* header */}
      <MDBox
        sx={{
          px: isMobile ? 1.4 : 2,
          py: isMobile ? 1.3 : 1.6,
          background: 'linear-gradient(180deg, rgba(0,150,136,.18), rgba(0,150,136,.06))',
        }}
      >
        <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
          <MDBox sx={{ minWidth: 0 }}>
            <MDTypography
              variant={isMobile ? 'h6' : 'h5'}
              fontWeight="bold"
              sx={{ lineHeight: 1.1 }}
            >
              Üyeler
            </MDTypography>
          </MDBox>
        </MDBox>

        {/* KPIs */}
        <MDBox
          sx={{
            mt: isMobile ? 1.05 : 1.35,
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))', // ✅ mobil 2x2
              sm: 'repeat(4, minmax(0, 1fr))', // ✅ desktop 4’lü
            },
            gap: isMobile ? 0.9 : 1.25,
          }}
        >
          <KpiPill icon="groups" label="Toplam" value={totalCount ?? 0} tone="primary" />
          <KpiPill icon="check_circle" label="Aktif" value={activeCount ?? 0} tone="success" />
          <KpiPill icon="cancel" label="Pasif" value={passiveCount ?? 0} tone="error" />
          <KpiPill icon="priority_high" label="Borçlu" value={debtCount ?? 0} tone="warning" />
        </MDBox>
      </MDBox>

      <Divider />
    </Card>
  );
}
